/**
 * CustomFontFamilyEditing Plugin for CKEditor
 *
 * This plugin manages the "font family" functionality for the CKEditor. It allows the application of custom 
 * font families to text within the editor. The plugin handles both the upcasting (when importing content) 
 * and downcasting (when exporting content), and also integrates a command to execute font-family changes.
 *
 * ## Features:
 * - **Font Family Selection**: 
 *   - Supports setting custom font families on text within the editor.
 *   - Uses `CustomFontFamily` as the model key to apply font family attributes to text nodes.
 * - **Editor Schema Modification**:
 *   - Extends the CKEditor model schema to allow the `CustomFontFamily` attribute on `$text` elements.
 * - **Upcasting**:
 *   - Converts `data-font-family` attributes from the HTML view into the model's `CustomFontFamily` attribute.
 * - **Downcasting**:
 *   - Converts the model's `CustomFontFamily` attribute back into `data-font-family` attributes when rendering HTML.
 * - **Font Family Command**:
 *   - Provides a command (`CustomFontFamilyCommand`) that can be executed to apply font family changes to selected text.
 *
 * ## Usage:
 * To apply a custom font family, you would use the `CustomFontFamilyCommand` via the editor's command API:
 * ```javascript
 * editor.execute(CKPlugins.CustomFontFamily, { value: 'Arial' });
 * ```
 * This applies the font family `Arial` to the selected text in the editor.
 *
 * ## Configuration:
 * The plugin can be configured with a list of available font families, which are used in the user interface to 
 * select a font family. These options are defined in `CUSTOM_FONT_FAMILY`.
 *
 * Example configuration:
 * ```javascript
 * editor.config.set(CKPlugins.CustomFontFamily, {
 *     options: CUSTOM_FONT_FAMILY,  // Array of available font families
 *     supportAllValues: true
 * });
 * ```
 * 
 * ## Implementation Details:
 * - The plugin integrates the `CustomFontFamilyCommand` to handle font family changes when executed.
 * - The `normalizeOptions` function ensures that all options are properly formatted and valid before they are used.
 * - The plugin supports upcasting and downcasting, ensuring font family attributes are properly preserved when 
 *   importing and exporting content.
 *
 * ## References:
 * - CKEditor Plugin API: https://ckeditor.com/docs/ckeditor5/latest/api/module_core_plugin-Plugin.html
 * - CKEditor Command API: https://ckeditor.com/docs/ckeditor5/latest/api/module_core_command-Command.html
 * 
 * @module CustomFontFamilyEditing
 * @exports CustomFontFamilyEditing
 */

import { CKPlugins } from '../../constants/constants';
import { CUSTOM_FONT_FAMILY } from '../map/font-family.map';
import { CustomFontFamilyCommand } from './fontFamilyCommand';

const Plugin = window.ClassicEditor.Plugin;

export function buildDefinition(
	modelAttributeKey,
	options
) {
	const definition = {
		model: {
			key: modelAttributeKey,
			values: []
		},
		view: {},
		upcastAlso: {}
	};

	for (const option of options) {
		definition.model.values.push(option.model);
		definition.view[option.model] = option.view;

		if ( option.upcastAlso ) {
			definition.upcastAlso[option.model] = option.upcastAlso;
		}
	}

	return definition;
}

function getOptionDefinition(option) {
	// Treat any object as full item definition provided by user in configuration.
	if (typeof option === 'object') {
		return option;
	}

	// Handle 'default' string as a special case. It will be used to remove the fontFamily attribute.
	if (option === 'default') {
		return {
			title: 'Default',
			model: undefined
		};
	}

	// Ignore values that we cannot parse to a definition.
	if (typeof option !== 'string') {
		return undefined;
	}
}

export function normalizeOptions(configuredOptions) {
	return configuredOptions
		.map(getOptionDefinition)
		.filter(option => option !== undefined) ;
}


export default class CustomFontFamilyEditing extends Plugin {
	static get pluginName() {
        return 'CustomFontFamilyEditing';
    }

    static get requires() {
        return [ CustomFontFamilyEditing ];
    }

    constructor(editor) {
        super(editor);

		editor.config.define(CKPlugins.CustomFontFamily, {
			options: CUSTOM_FONT_FAMILY,
			supportAllValues: true
		} );

        // Extend schema to allow font-family attribute on text nodes
        editor.model.schema.extend('$text', {
            allowAttributes: [CKPlugins.CustomFontFamily]
        });

        // Define attribute properties for font-family
        editor.model.schema.setAttributeProperties(CKPlugins.CustomFontFamily, {
            isFormatting: true,
            copyOnEnter: true
        });

        // Set up upcasting (to read font-family classes from HTML)
        editor.conversion.for('upcast').elementToAttribute({
            view: {
                name: 'span',
                attributes: {
                    'data-font-family': true
                }
            },
            model: {
                key: CKPlugins.CustomFontFamily,
                value: viewElement => viewElement.getAttribute('data-font-family')
            }
        });

        // Set up downcasting (to apply font-family class in the view)
        editor.conversion.for('downcast').attributeToElement({
            model: CKPlugins.CustomFontFamily,
            view: (modelValue, { writer }) => {
                return writer.createAttributeElement('span', {
                    'data-font-family': modelValue
                });
            }
        });

        // Add the font family command
        editor.commands.add(CKPlugins.CustomFontFamily, new CustomFontFamilyCommand(editor));
    }

    init() {
        const editor = this.editor;

        // Allow the font family attribute on text nodes
        editor.model.schema.extend('$text', { allowAttributes: [CKPlugins.CustomFontFamily] });
    }
}
